import { useState, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";

import { Input } from "@/shared/ui/input";

/**
 * 一组可增删的词。信号订阅里"关键词 / 地区"都用它。
 * ★ 回车即加、× 即删,没有"编辑模式"也没有保存二次确认 ——
 *   订阅页的产品承诺是**只配一次**,多一道模式切换就多一个不会有人走完的流程。
 */
export function ChipInput({
  label,
  hint,
  placeholder,
  values,
  onChange,
  max,
}: {
  label: string;
  hint?: string;
  placeholder?: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** 最多几个(给了才限):到数就不让再加,标题旁摆「n/max」,超了(老数据)标红。 */
  max?: number;
}) {
  const [draft, setDraft] = useState("");
  const full = max != null && values.length >= max;
  const over = max != null && values.length > max;

  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v) || full) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-semibold">{label}</span>
        {max != null && (
          <span
            data-testid="chip-count"
            className={
              over ? "text-2xs font-semibold text-destructive" : "text-2xs text-muted-foreground"
            }
          >
            {values.length}/{max}
          </span>
        )}
        {hint && <span className="text-2xs text-muted-foreground">{hint}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.length === 0 && <span className="text-xs text-muted-foreground">还没有</span>}
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full border bg-background py-1 pl-2.5 pr-1 text-xs"
          >
            {v}
            <button
              type="button"
              aria-label={`删除 ${v}`}
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="grid h-4 w-4 place-items-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex max-w-sm gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={full ? `最多 ${max} 个，删掉一个再加` : placeholder}
          disabled={full}
          aria-label={`添加${label}`}
          className="h-9 text-sm"
        />
        <button
          type="button"
          onClick={add}
          disabled={full}
          className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border px-3 text-xs font-semibold transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          加上
        </button>
      </div>
    </div>
  );
}
